import React, { useState } from 'react';
import { Box, Typography, Chip, TextField, InputAdornment, Button, Tooltip } from '@mui/material';
import {
  Search as SearchIcon,
  Timeline as ActivityIcon,
  LibraryMusic as AudioListIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { MOCK_SPECIES_CATALOG_EXTENDED } from '../bioacousticMockData';

interface SpeciesCatalogProps {
  summary?: {
    totalSpecies: number;
    totalDetections: number;
    avgConfidence: number;
    filesProcessed: number;
    filesTotal: number;
  };
  onAvgActivityTrend?: (species: typeof MOCK_SPECIES_CATALOG_EXTENDED[0]) => void;
  onAnalyzeLogs?: (species: typeof MOCK_SPECIES_CATALOG_EXTENDED[0]) => void;
  onViewInAcousticData?: (speciesName: string) => void;
}

const DEFAULT_SUMMARY = {
  totalSpecies: 23,
  totalDetections: 1380,
  avgConfidence: 91,
  filesProcessed: 50,
  filesTotal: 50,
};

const TYPE_COLOR: Record<string, string> = {
  AVIAN: '#10B981',
  POLLINATOR: '#06B6D4',
  BAT: '#F59E0B',
  INSECT: '#06B6D4',
  MAMMAL: '#F59E0B',
};

const THREAT_COLOR: Record<string, string> = {
  Endangered: '#00D4FF',
  Vulnerable: '#8B5CF6',
  'Least Concerned': '#10B981',
  Unknown: '#F97316',
};

const FILTER_TABS = [
  { id: 'AVIAN', label: 'AVIAN', disabled: false },
  { id: 'POLLINATOR', label: 'POLLINATOR (Coming Soon)', disabled: true },
  { id: 'BAT', label: 'BAT (Coming Soon)', disabled: true },
];

const SpeciesCatalog: React.FC<SpeciesCatalogProps> = ({
  summary = DEFAULT_SUMMARY,
  onAvgActivityTrend,
  onAnalyzeLogs,
  onViewInAcousticData,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('AVIAN');

  const filteredSpecies = MOCK_SPECIES_CATALOG_EXTENDED.filter((sp) => {
    const matchesSearch =
      sp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sp.commonName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sp.family.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = sp.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', pb: 4 }}>
      {/* Section Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontSize: '10px', color: '#10B981', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 800, mb: 0.5 }}>
            Bio-Inventory Report
          </Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
            Species Catalog
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#888', mt: 0.5 }}>
            Cross-referenced database of acoustic signatures and taxonomical classifications at this station. System confidence level: {summary.avgConfidence}%.
          </Typography>
        </Box>
      </Box>

      {/* Summary Stats Row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        {[
          { label: 'Total Species', value: summary.totalSpecies, sub: 'Unique avian species identified', color: '#10B981' },
          { label: 'Total Detections', value: summary.totalDetections, sub: 'Across all audio files', color: '#00D4FF' },
          { label: 'Avg Confidence', value: `${summary.avgConfidence}%`, sub: 'Across all detections', color: '#10B981' },
          { label: 'Files Processed', value: `${summary.filesProcessed}/${summary.filesTotal}`, sub: '100% cataloged', color: '#10B981' },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid #1E1E1E',
              borderRadius: '10px',
              p: '16px 18px',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'rgba(16,185,129,0.3)' },
            }}
          >
            <Typography sx={{ fontSize: '10px', color: '#666', letterSpacing: '0.8px', mb: 0.5, textTransform: 'uppercase', fontWeight: 700 }}>
              {stat.label}
            </Typography>
            <Typography sx={{ fontSize: '28px', fontWeight: 800, color: stat.color, lineHeight: 1 }}>
              {stat.value}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#444', mt: 0.75 }}>{stat.sub}</Typography>
          </Box>
        ))}
      </Box>

      {/* Search and Filters Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search species by name or family..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '320px',
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgba(255, 255, 255, 0.02)',
              borderRadius: '8px',
              border: '1px solid #222',
              color: '#fff',
              fontSize: '12px',
              height: '36px',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.04)' },
            },
          }}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          {FILTER_TABS.map((tab) => {
            if (tab.disabled) {
              return (
                <Tooltip key={tab.id} title="Taxonomic model coming soon for bioacoustic classification" arrow>
                  <span>
                    <Button
                      size="small"
                      disabled
                      startIcon={<LockIcon sx={{ fontSize: 11, color: '#555' }} />}
                      sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '6px',
                        border: '1px dashed #282828',
                        color: '#555 !important',
                        bgcolor: 'rgba(255,255,255,0.01)',
                        opacity: 0.55,
                        cursor: 'not-allowed',
                      }}
                    >
                      {tab.label}
                    </Button>
                  </span>
                </Tooltip>
              );
            }

            const isSelected = typeFilter === tab.id;
            return (
              <Button
                key={tab.id}
                size="small"
                onClick={() => setTypeFilter(tab.id)}
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '6px',
                  border: `1px solid ${isSelected ? '#10B981' : '#222'}`,
                  color: isSelected ? '#10B981' : '#888',
                  bgcolor: isSelected ? 'rgba(16,185,129,0.1)' : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(16,185,129,0.15)',
                    color: '#fff',
                  },
                }}
              >
                {tab.label}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* Species Card Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {filteredSpecies.map((species) => {
          const color = TYPE_COLOR[species.type] || '#10B981';
          const threatColor = THREAT_COLOR[species.threatLevel || 'Least Concerned'] || '#10B981';
          return (
            <Box
              key={species.name}
              sx={{
                background: 'linear-gradient(180deg, rgba(18, 24, 21, 0.85) 0%, rgba(10, 14, 12, 0.95) 100%)',
                border: '1px solid #1E2E25',
                borderRadius: '12px',
                p: '18px',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'rgba(16,185,129,0.45)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                },
              }}
            >
              {/* Header Badges: Threat Level & Taxonomical Type */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip
                  label={species.threatLevel || 'Least Concerned'}
                  size="small"
                  sx={{
                    height: '22px',
                    fontSize: '9px',
                    fontWeight: 800,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    bgcolor: `${threatColor}18`,
                    color: threatColor,
                    border: `1px solid ${threatColor}44`,
                  }}
                />
                <Chip
                  label={species.type}
                  size="small"
                  sx={{
                    height: '22px',
                    fontSize: '9px',
                    fontWeight: 800,
                    letterSpacing: '0.5px',
                    bgcolor: 'rgba(16, 185, 129, 0.12)',
                    color: '#10B981',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                  }}
                />
              </Box>

              {/* Species Names */}
              <Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#f0fdf4', lineHeight: 1.2 }}>
                  {species.name}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.6px', mt: 0.5, fontWeight: 600 }}>
                  {species.commonName}
                </Typography>
              </Box>

              {/* Family & Bioacoustic Stats */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'rgba(255,255,255,0.02)', p: 1.25, borderRadius: '8px', border: '1px solid #1A1A1A' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '10px', color: '#555', textTransform: 'uppercase', fontWeight: 700 }}>FAMILY</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#aaa', fontStyle: 'italic' }}>{species.family}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '10px', color: '#555', textTransform: 'uppercase', fontWeight: 700 }}>CONFIDENCE</Typography>
                  <Typography sx={{ fontSize: '11px', color: '#10B981', fontWeight: 700 }}>{species.confidence}%</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '10px', color: '#555', textTransform: 'uppercase', fontWeight: 700 }}>DETECTIONS</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, ml: 2 }}>
                    <Box sx={{ height: '5px', flex: 1, background: '#1F2922', borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${Math.min(100, (species.detections / 140) * 100)}%`, background: color, borderRadius: 1 }} />
                    </Box>
                    <Typography sx={{ fontSize: '11px', color, fontWeight: 800 }}>{species.detections}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Sample Audio Snippet */}
              {species.audioSample && (
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.35)', border: '1px solid #1E2923', borderRadius: '8px', p: 1.25 }}>
                  <Typography sx={{ fontSize: '9px', color: '#777', fontWeight: 600 }}>
                    AUDIO LOG: <span style={{ color: '#10B981' }}>{species.audioSample.fileName}</span>
                  </Typography>
                  <Typography sx={{ fontSize: '9px', color: '#555', mt: 0.25 }}>
                    Duration: {species.audioSample.duration} • Sample Rate: 48kHz
                  </Typography>
                </Box>
              )}

              {/* Dual Action Buttons: Avg Activity Trend + Fetched Audios */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 'auto', pt: 0.5 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<ActivityIcon sx={{ fontSize: 13 }} />}
                  onClick={() => {
                    if (onAvgActivityTrend) onAvgActivityTrend(species);
                    else if (onAnalyzeLogs) onAnalyzeLogs(species);
                  }}
                  sx={{
                    fontSize: '9.5px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    color: '#10B981',
                    borderColor: 'rgba(16, 185, 129, 0.35)',
                    bgcolor: 'rgba(16, 185, 129, 0.06)',
                    py: 0.75,
                    px: 0.5,
                    minWidth: 0,
                    borderRadius: '6px',
                    '& .MuiButton-startIcon': { mr: '4px', ml: 0 },
                    '&:hover': {
                      borderColor: '#10B981',
                      bgcolor: 'rgba(16, 185, 129, 0.18)',
                      color: '#fff',
                    },
                  }}
                >
                  Avg Activity Trend
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<AudioListIcon sx={{ fontSize: 13 }} />}
                  onClick={() => {
                    if (onViewInAcousticData) onViewInAcousticData(species.commonName || species.name);
                  }}
                  sx={{
                    fontSize: '9.5px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    color: '#00D4FF',
                    borderColor: 'rgba(0, 212, 255, 0.3)',
                    bgcolor: 'rgba(0, 212, 255, 0.05)',
                    py: 0.75,
                    px: 0.5,
                    minWidth: 0,
                    borderRadius: '6px',
                    '& .MuiButton-startIcon': { mr: '4px', ml: 0 },
                    '&:hover': {
                      borderColor: '#00D4FF',
                      bgcolor: 'rgba(0, 212, 255, 0.15)',
                      color: '#fff',
                    },
                  }}
                >
                  Fetched Audios
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SpeciesCatalog;
